import clsx from 'clsx';
import { Heading, Text } from '@mysten/ui';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ClobTradeRow } from '@cpc/tx';
import type { BookLevel } from '~/hooks/usePushHub';
import { useT } from '~/i18n/LocaleContext';
import { Card } from '~/ui/Card';
import { ListBulletIcon } from '@heroicons/react/24/outline';

import {
    DEPTH_ROW_PX,
    DEPTH_VISIBLE_ROWS,
    depthPaneHeightPx,
    formatLastTradePrice,
    formatPrice,
    tradeMidStripShellCls,
    tradeMidStripToneCls,
    tradeMidStripWrapCls,
    tradeScrollCls,
} from './tradeTheme';
import { formatClobQtyLots } from '~/config/clobConfig';

export { DEPTH_VISIBLE_ROWS };

function sortLevels(levels: BookLevel[], side: 'bid' | 'ask'): BookLevel[] {
    return side === 'ask'
        ? [...levels].sort((a, b) => (BigInt(a.priceTicks) > BigInt(b.priceTicks) ? 1 : -1))
        : [...levels].sort((a, b) => (BigInt(b.priceTicks) > BigInt(a.priceTicks) ? 1 : -1));
}

function DepthScrollPane({
    rows,
    side,
    maxQty,
    onPick,
    pinToEnd,
}: {
    rows: BookLevel[];
    side: 'bid' | 'ask';
    maxQty: bigint;
    onPick: (price: string) => void;
    /** 卖盘：滚动条默认贴底，最优卖价靠近中间价 */
    pinToEnd?: boolean;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const paneH = depthPaneHeightPx();

    useLayoutEffect(() => {
        if (!pinToEnd) return;
        const el = ref.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight - el.clientHeight;
    }, [pinToEnd, rows]);

    return (
        <div
            ref={ref}
            /* PC 端安全适配核心：
               1. 引入 max-md:!h-[140px] 意思是：在小于 768px（移动端）屏幕下，用 ! 强行覆盖 style 里的固定高度。
               2. 这样在 PC 端，!h 并不生效，系统会 100% 纯净地走 style={{ height: paneH }} 原有高度逻辑。
               3. [&::-webkit-scrollbar]:hidden 隐藏 H5 原生滚动条。
            */
            className={clsx(
                tradeScrollCls,
                'shrink-0 [&::-webkit-scrollbar]:hidden max-md:!h-[140px]'
            )}
            style={{ height: paneH }}
        >
            <div className={clsx('flex flex-col', pinToEnd && 'min-h-full justify-end')}>
                {rows.map((l) => (
                    <DepthRowView
                        key={`${side}-${l.priceTicks}`}
                        row={l}
                        side={side}
                        maxQty={maxQty}
                        onPick={onPick}
                    />
                ))}
            </div>
        </div>
    );
}

export function TradeOrderBook({
    bids,
    asks,
    markPrice,
    lastTradeSide,
    onPickPrice,
    baseSymbol = 'DGC',
    quoteSymbol = 'DATA',
}: {
    bids: BookLevel[];
    asks: BookLevel[];
    /** 最新成交价（与顶部 ticker 同源） */
    markPrice: string;
    /** 最新一笔成交方向，用于中间价涨跌色 */
    lastTradeSide?: ClobTradeRow['tradeSide'];
    onPickPrice: (dataPerDgc: string) => void;
    baseSymbol?: string;
    quoteSymbol?: string;
}) {
    const t = useT();
    const sortedAsks = useMemo(() => sortLevels(asks, 'ask'), [asks]);
    const sortedBids = useMemo(() => sortLevels(bids, 'bid'), [bids]);
    const displayAsks = useMemo(() => [...sortedAsks].reverse(), [sortedAsks]);

    const bestBid = sortedBids[0]?.dataPerDgc;
    const bestAsk = sortedAsks[0]?.dataPerDgc;
    const midPrice = midPriceLabel(sortedBids, sortedAsks, markPrice);

    const { spread, spreadPct } = useMemo(() => {
        if (!bestBid || !bestAsk) return { spread: null, spreadPct: null };
        const bidN = Number.parseFloat(bestBid);
        const askN = Number.parseFloat(bestAsk);
        if (!Number.isFinite(bidN) || !Number.isFinite(askN) || askN <= bidN) {
            return { spread: null, spreadPct: null };
        }
        const s = askN - bidN;
        return { spread: s, spreadPct: bidN > 0 ? (s / bidN) * 100 : null };
    }, [bestBid, bestAsk]);

    const priceUp = lastTradeSide === 'buy';
    const priceDown = lastTradeSide === 'sell';
    const maxQty = useMemo(() => {
        let m = 0n;
        for (const row of [...sortedAsks, ...sortedBids]) {
            const q = BigInt(row.qtyLots || '0');
            if (q > m) m = q;
        }
        return m;
    }, [sortedAsks, sortedBids]);

    return (
        /* PC 端保持 height="full" 和 growOnHover。
           仅仅针对 H5 容器添加 max-md:h-auto 避免外层盒子在移动端死死撑开。
        */
        <Card bg="dark" spacing="sm" height="full" growOnHover className="max-md:h-auto">
            <div className="flex flex-col w-full select-none">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                    <Heading variant="heading4/semibold" color="dark">
                        {t('trade.orderBook')}
                    </Heading>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#7bd0ff]/20">
                        <ListBulletIcon className="h-4 w-4 text-[#7bd0ff]" />
                    </div>
                </div>

                <div className="mb-1 grid grid-cols-2 gap-1 px-0.5">
                    <Text variant="caption/semibold" color="dark">
                        {t('common.price')}({quoteSymbol})
                    </Text>
                    <Text variant="caption/semibold" color="dark" className="text-right">
                        {t('common.quantity')}({baseSymbol})
                    </Text>
                </div>

                <DepthScrollPane
                    rows={displayAsks}
                    side="ask"
                    maxQty={maxQty}
                    onPick={onPickPrice}
                    pinToEnd
                />

                <div className={tradeMidStripWrapCls}>
                    <OrderBookMidPrice
                        markPrice={markPrice}
                        midPrice={midPrice}
                        spread={spread}
                        spreadPct={spreadPct}
                        priceUp={priceUp}
                        priceDown={priceDown}
                        onPick={onPickPrice}
                    />
                </div>

                <DepthScrollPane rows={sortedBids} side="bid" maxQty={maxQty} onPick={onPickPrice} />
            </div>
        </Card>
    );
}

/** 买卖盘之间的最新价行：方向渐变 + 副信息 + 成交闪烁 */
function OrderBookMidPrice({
    markPrice,
    midPrice,
    spread,
    spreadPct,
    priceUp,
    priceDown,
    onPick,
}: {
    markPrice: string;
    midPrice: string;
    spread: number | null;
    spreadPct: number | null;
    priceUp: boolean;
    priceDown: boolean;
    onPick: (price: string) => void;
}) {
    const t = useT();
    const prevPrice = useRef(markPrice);
    const [flash, setFlash] = useState<'up' | 'down' | null>(null);

    useEffect(() => {
        if (prevPrice.current === markPrice) return;
        const prev = Number.parseFloat(prevPrice.current);
        const next = Number.parseFloat(markPrice);
        prevPrice.current = markPrice;
        if (!Number.isFinite(prev) || !Number.isFinite(next) || prev === next) return;
        setFlash(next >= prev ? 'up' : 'down');
        const t = window.setTimeout(() => setFlash(null), 480);
        return () => window.clearTimeout(t);
    }, [markPrice]);

    const accent = priceUp ? 'up' : priceDown ? 'down' : 'neutral';
    const tone = tradeMidStripToneCls[accent];

    const showMidHint = midPrice !== markPrice && midPrice !== '—';

    return (
        <button
            type="button"
            className={clsx(
                tradeMidStripShellCls,
                tone.shell,
                'group relative flex items-center justify-between w-full overflow-hidden text-left transition-[box-shadow,filter] duration-300',
                'bg-gradient-to-r',
                tone.wash,
                flash === 'up' &&
                    'shadow-[0_0_22px_rgba(14,203,129,0.35),inset_0_0_0_1px_rgba(14,203,129,0.55)]',
                flash === 'down' &&
                    'shadow-[0_0_22px_rgba(246,70,93,0.35),inset_0_0_0_1px_rgba(246,70,93,0.55)]',
                'md:hover:brightness-110 active:brightness-95',
            )}
            style={{ minHeight: DEPTH_ROW_PX + 12 }}
            onClick={() => onPick(markPrice)}
            title={t('trade.clickFillPrice')}
        >
            <span
                className={clsx('absolute bottom-2 left-0 top-2 w-[4px] rounded-r-sm', tone.bar)}
                aria-hidden
            />

            {/* 左侧：最新价 */}
            <div className="relative z-10 flex min-w-0 flex-col justify-center gap-0.5 py-1.5 pl-3 pr-1">
                <span className="flex min-w-0 items-center gap-1">
                    <span
                        className={clsx(
                            'inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded text-[8px] font-bold leading-none',
                            tone.badge,
                        )}
                        aria-hidden
                    >
                        {priceUp ? '▲' : priceDown ? '▼' : '◆'}
                    </span>
                    <span
                        className={clsx(
                            'truncate text-[15px] md:text-[16px] font-bold tabular-nums leading-none tracking-tight',
                            tone.price,
                            flash != null && 'animate-pulse',
                        )}
                    >
                        {markPrice}
                    </span>
                </span>
                <span className={clsx('pl-[18px] text-[8px] font-semibold uppercase tracking-widest', tone.label)}>
                    Last
                </span>
            </div>

            {/* 右侧：Mid 与 价差 */}
            <div className="relative z-10 flex flex-col items-end justify-center gap-0.5 py-1.5 pl-1 pr-2 text-right min-w-0 shrink-0">
                {showMidHint && (
                    <span className={clsx('text-[10px] tabular-nums leading-none truncate max-w-full', tone.meta)}>
                        Mid{' '}
                        <span className="font-semibold text-[#EAECEF]">{midPrice}</span>
                    </span>
                )}
                {spread != null ? (
                    <span className={clsx('text-[10px] tabular-nums leading-none truncate max-w-full', tone.meta)}>
                        {t('common.spread')}{' '}
                        <span className="font-semibold text-[#EAECEF]">
                            {formatLastTradePrice(String(spread))}
                        </span>
                        {spreadPct != null && (
                            <span className="opacity-80 text-[9px] md:text-[10px]"> ({spreadPct.toFixed(2)}%)</span>
                        )}
                    </span>
                ) : (
                    !showMidHint && <span className="text-[10px] text-[#5E6673]">—</span>
                )}
            </div>
        </button>
    );
}

function DepthRowView({
    row,
    side,
    maxQty,
    onPick,
}: {
    row: BookLevel;
    side: 'bid' | 'ask';
    maxQty: bigint;
    onPick: (price: string) => void;
}) {
    const isAsk = side === 'ask';
    const qty = BigInt(row.qtyLots || '0');
    const pct = maxQty > 0n ? Number((qty * 100n) / maxQty) : 0;

    return (
        <button
            type="button"
            className="relative grid w-full grid-cols-2 items-center px-0.5 text-left active:bg-white/10 transition-colors duration-150"
            style={{ height: DEPTH_ROW_PX }}
            onClick={() => onPick(row.dataPerDgc)}
        >
            <span
                className="absolute inset-y-0 right-0 opacity-20"
                style={{
                    width: `${Math.max(pct, qty > 0n ? 4 : 0)}%`,
                    backgroundColor: isAsk ? '#ef4444' : '#10b981',
                }}
            />
            <span
                className={clsx(
                    'relative z-10 text-bodySmall tabular-nums truncate pr-1',
                    isAsk ? 'text-[#ef4444]' : 'text-[#10b981]',
                )}
            >
                {formatLastTradePrice(row.dataPerDgc)}
            </span>
            <span className="relative z-10 text-right text-bodySmall tabular-nums text-white/80 truncate pl-1">
                {formatClobQtyLots(row.qtyLots)}
            </span>
        </button>
    );
}

/** @deprecated 盘口中间已改用最新成交价；保留供测试或外部引用 */
export function midPriceLabel(bids: BookLevel[], asks: BookLevel[], fallback: string): string {
    const bid = sortLevels(bids, 'bid')[0]?.dataPerDgc;
    const ask = sortLevels(asks, 'ask')[0]?.dataPerDgc;
    if (bid && ask) {
        const mid = (Number.parseFloat(bid) + Number.parseFloat(ask)) / 2;
        return formatPrice(Number.isFinite(mid) ? mid : 0);
    }
    return fallback;
}